import { supabase } from '@/lib/supabase';
import { imageService } from './image';

export interface RestaurantImage {
  id: string;
  restaurant_id: string;
  image_url: string;
  sequence: number;
  description?: string;
  uploaded_by?: string;
  created_at: string;
  updated_at: string;
}

export interface RestaurantImageCreate {
  restaurant_id: string;
  image_url: string;
  sequence: number;
  description?: string;
  uploaded_by?: string;
}

class RestaurantImageService {
  
  /**
   * 매장의 이미지 목록 가져오기 (sequence 순으로 정렬)
   */
  async getRestaurantImages(restaurantId: string): Promise<{ success: boolean; data?: RestaurantImage[]; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('restaurant_images')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .order('sequence', { ascending: true });

      if (error) {
        console.error('Get restaurant images error:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Get restaurant images error:', error);
      return { success: false, error: '매장 이미지를 불러오는 중 오류가 발생했습니다.' };
    }
  }

  /**
   * 매장 이미지 추가 (최대 3장, 자동 sequence 할당)
   */
  async addRestaurantImage(
    restaurantId: string, 
    imageFile: File, 
    userId?: string,
    description?: string
  ): Promise<{ success: boolean; data?: RestaurantImage; error?: string }> {
    try {
      // 현재 이미지 개수 확인
      const { data: existingImages, error: countError } = await supabase
        .from('restaurant_images')
        .select('sequence')
        .eq('restaurant_id', restaurantId)
        .order('sequence', { ascending: true });

      if (countError) {
        console.error('Count images error:', countError);
        return { success: false, error: countError.message };
      }

      if (existingImages && existingImages.length >= 3) {
        return { success: false, error: '매장당 최대 3장의 이미지만 등록할 수 있습니다.' };
      }

      // 다음 sequence 계산
      const nextSequence = existingImages && existingImages.length > 0 
        ? Math.max(...existingImages.map(img => img.sequence)) + 1 
        : 1;

      // 이미지 업로드
      const fileName = `${Date.now()}_${imageFile.name}`;
      const filePath = `restaurants/${fileName}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('bucket1')
        .upload(filePath, imageFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Image upload error:', uploadError);
        return { success: false, error: '이미지 업로드에 실패했습니다.' };
      }

      // DB에 이미지 정보 저장
      const imageUrl = imageService.getPublicImageUrl(uploadData.path);
      
      const { data, error } = await supabase
        .from('restaurant_images')
        .insert({
          restaurant_id: restaurantId,
          image_url: imageUrl,
          sequence: nextSequence,
          description,
          uploaded_by: userId
        })
        .select()
        .single();

      if (error) {
        // DB 저장 실패 시 업로드된 이미지 삭제
        await imageService.deleteImage(uploadData.path);
        console.error('Save restaurant image error:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Add restaurant image error:', error);
      return { success: false, error: '매장 이미지 추가 중 오류가 발생했습니다.' };
    }
  }

  /**
   * 매장 이미지 삭제 (Storage와 DB 모두 삭제)
   */
  async deleteRestaurantImage(imageId: string): Promise<{ success: boolean; error?: string }> {
    try {
      // 삭제할 이미지 정보 가져오기
      const { data: imageToDelete, error: getError } = await supabase
        .from('restaurant_images')
        .select('*')
        .eq('id', imageId)
        .single();

      if (getError) {
        console.error('Get image to delete error:', getError);
        return { success: false, error: getError.message };
      }

      // Storage에서 이미지 파일 삭제
      const deleted = await imageService.deleteImageByUrl(imageToDelete.image_url);
      if (!deleted) {
        console.warn('Failed to delete image from storage, but continuing with DB deletion');
      }

      // DB에서 이미지 레코드 삭제
      const { error: deleteError } = await supabase
        .from('restaurant_images')
        .delete()
        .eq('id', imageId);

      if (deleteError) {
        console.error('Delete restaurant image error:', deleteError);
        return { success: false, error: deleteError.message };
      }

      // 남은 이미지들의 sequence 재정렬
      await this.reorderSequences(imageToDelete.restaurant_id);

      return { success: true };
    } catch (error) {
      console.error('Delete restaurant image error:', error);
      return { success: false, error: '매장 이미지 삭제 중 오류가 발생했습니다.' };
    }
  }

  /**
   * 매장 이미지 순서 변경
   */
  async updateImageSequence(
    imageId: string, 
    newSequence: number
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // 이미지 정보 확인
      const { data: image, error: getError } = await supabase
        .from('restaurant_images')
        .select('restaurant_id, sequence')
        .eq('id', imageId)
        .single();

      if (getError) {
        console.error('Get image error:', getError);
        return { success: false, error: getError.message };
      }

      // 새 sequence 유효성 검사 (1-3 범위)
      if (newSequence < 1 || newSequence > 3) {
        return { success: false, error: 'sequence는 1-3 범위여야 합니다.' };
      }

      // 해당 sequence를 사용하는 다른 이미지와 순서 교체
      const { error: swapError } = await supabase.rpc('swap_image_sequences', {
        image_id_1: imageId,
        new_sequence_1: newSequence,
        restaurant_id_param: image.restaurant_id
      });

      if (swapError) {
        console.error('Swap sequences error:', swapError);
        return { success: false, error: swapError.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Update image sequence error:', error);
      return { success: false, error: '이미지 순서 변경 중 오류가 발생했습니다.' };
    }
  }

  /**
   * sequence 재정렬 (삭제 후 빈 공간 메우기)
   */
  private async reorderSequences(restaurantId: string): Promise<void> {
    try {
      const { data: images, error } = await supabase
        .from('restaurant_images')
        .select('id, sequence')
        .eq('restaurant_id', restaurantId)
        .order('sequence', { ascending: true });

      if (error || !images || images.length === 0) return;

      // sequence를 1, 2, 3으로 재정렬
      const updates = images.map((img, index) => ({
        id: img.id,
        sequence: index + 1
      }));

      for (const update of updates) {
        await supabase
          .from('restaurant_images')
          .update({ sequence: update.sequence })
          .eq('id', update.id);
      }
    } catch (error) {
      console.error('Reorder sequences error:', error);
    }
  }

  /**
   * 매장의 대표 이미지 가져오기 (sequence = 1)
   */
  async getMainRestaurantImage(restaurantId: string): Promise<{ success: boolean; data?: RestaurantImage; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('restaurant_images')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .eq('sequence', 1)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned - this is ok
          return { success: true, data: undefined };
        }
        console.error('Get main restaurant image error:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Get main restaurant image error:', error);
      return { success: false, error: '대표 이미지를 불러오는 중 오류가 발생했습니다.' };
    }
  }
}

export const restaurantImageService = new RestaurantImageService();